import React, { useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { FileText, Download, Search, Scale, Gavel, Shield, ClipboardCheck, Users, Compass, Award, BookMarked, ArrowRight, Lock } from "lucide-react";
import { MANUAL_V2, GROUPS_V2, APPENDICES_V2, APPENDICES_PAGE, REFERENCES_PAGE } from "@/data/policiesV2";
import { KeyRules, Contacts, FOCUS } from "@/components/policies/KeyRules";

const ICONS = { Compass, Scale, ClipboardCheck, Shield, Users, Gavel, Award, BookMarked };
export const RELEASED_PDF = "/documents/pgdcsl-programme-governance-manual.pdf?v=2.0-final-05";
export const DOWNLOAD_NAME = "GCSB-PGDCSL-Programme-Governance-Manual-v2.0.pdf";
const tid = (s) => s.replace(/\s|-/g, "").toLowerCase();

const Meta = ({ k, v }) => (
  <div><dt className="text-[10px] uppercase tracking-widest text-zinc-400">{k}</dt><dd className="text-sm text-white">{v}</dd></div>
);

const PageLink = ({ href, page, label, testid, className = "" }) => (
  <a href={`${href}#page=${page}`} target="_blank" rel="noreferrer" className={`${className} ${FOCUS}`} data-testid={testid}
     aria-label={`${label}: open manual at page ${page} (opens in a new tab)`}>
    Open manual at p. {page} <ArrowRight size={11} aria-hidden="true"/>
  </a>
);

export default function PoliciesV2Page({ viewUrl = RELEASED_PDF, downloadUrl = RELEASED_PDF, preview = false }) {
  useSeo({
    title: "Programme & Governance Policies - GCSB-controlled PGDCSL rules, CSI delivery",
    description: "PGDCSL programme and governance policies: rules controlled by the Global Converged Security Board (GCSB), with learning delivered by the Converged Security Institute (CSI).",
    path: "/policies", noindex: preview,
  });
  const [query, setQuery] = useState("");
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GROUPS_V2;
    return GROUPS_V2.map((g) => ({ ...g, items: g.items.filter((it) => [it.title, it.code, it.part, it.summary].some((s) => s.toLowerCase().includes(q))) }))
      .filter((g) => g.items.length > 0);
  }, [query]);

  return (
    <div data-testid="policies-page">
      {preview && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs sm:text-sm px-4 py-2 text-center inline-flex w-full justify-center items-center gap-2" role="status" data-testid="policies-preview-banner">
          <Lock size={14} aria-hidden="true"/> Private admin preview of the Version 2.0 page. Not released: the public /policies page and PDF are unchanged.
        </div>
      )}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="eyebrow mb-3">Governance & Standards</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="policies-h1">
          Programme &amp; <span className="italic text-brand-gold">Governance Policies</span>
        </h1>
        <p className="mt-4 text-sm sm:text-base text-zinc-700 max-w-3xl leading-relaxed" data-testid="policies-ownership-statement">
          The PGDCSL programme and governance framework is controlled by the Global Converged Security Board (GCSB). Converged Security Institute (CSI) is a separate education and learning-delivery provider. Qualification, assessment, credential and appeal decisions follow the applicable GCSB rules and independent governance.
        </p>
        <p className="mt-2 text-sm text-zinc-600 max-w-3xl" data-testid="policies-ip-statement">{MANUAL_V2.owner}</p>

        <div className="mt-6 rounded-2xl bg-[var(--navy-deep)] text-white p-5 sm:p-6" data-testid="manual-callout">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[var(--gold-deep)]/90 grid place-items-center shrink-0"><FileText className="text-white" size={22} aria-hidden="true"/></div>
            <div className="flex-1 min-w-0">
              <div className="eyebrow text-[var(--gold-soft)]">Master document</div>
              <div className="font-display text-xl sm:text-2xl leading-tight">{MANUAL_V2.title}</div>
              <div className="text-xs text-zinc-300 mt-1">{MANUAL_V2.contents}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <a href={viewUrl} target="_blank" rel="noreferrer" className={`chip inline-flex items-center gap-1.5 bg-white/5 border-white/20 text-white hover:bg-white/10 ${FOCUS}`} data-testid="view-manual-pdf">
                <FileText size={13} aria-hidden="true"/> View in browser<span className="sr-only"> (opens in a new tab)</span>
              </a>
              <a href={downloadUrl} download={DOWNLOAD_NAME} className={`btn-terracotta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${FOCUS}`} data-testid="download-manual-pdf">
                <Download size={13} aria-hidden="true"/> Download PDF
              </a>
            </div>
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 mt-5 pt-5 border-t border-white/10" data-testid="manual-meta">
            <Meta k="Reference" v={MANUAL_V2.reference}/>
            <Meta k="Effective" v="01/01/2027"/>
            <Meta k="Next review" v="01/01/2028"/>
          </dl>
        </div>

        <div className="mt-6 relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden="true"/>
          <label htmlFor="policy-search" className="sr-only">Search policies</label>
          <input id="policy-search" type="search" placeholder="Search policies, codes or topics…" value={query} onChange={(e) => setQuery(e.target.value)}
                 style={{ paddingLeft: "2.5rem" }} className={`w-full rounded-full border border-[var(--border-warm)] bg-white pr-4 py-2 text-sm outline-none focus:border-[var(--navy)] ${FOCUS}`} data-testid="policy-search"/>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 space-y-10">
        {groups.length === 0 && <div className="text-sm text-zinc-500" data-testid="policy-search-empty">No policies match "{query}".</div>}
        {groups.map((g) => {
          const Icon = ICONS[g.icon];
          return (
            <div key={g.id} id={g.id} data-testid={`group-${g.id}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[var(--navy)] text-[var(--gold-soft)] grid place-items-center shrink-0"><Icon size={18} aria-hidden="true"/></div>
                <div>
                  <div className="eyebrow">{g.items.length} document{g.items.length === 1 ? "" : "s"}</div>
                  <h2 className="font-display text-2xl sm:text-3xl leading-tight">{g.title}</h2>
                  <p className="text-sm text-zinc-700 mt-1 max-w-3xl">{g.description}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {g.items.map((it) => (
                  <div key={it.code} className="card-warm p-5 flex flex-col gap-2" data-testid={`policy-${tid(it.code)}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="inline-flex items-center rounded-full bg-[var(--muted-surface)] border border-[var(--border-warm)] px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase text-[var(--navy)] break-all">
                        {it.part} · {it.code}
                      </span>
                      <PageLink href={viewUrl} page={it.page} label={it.title} testid={`open-${tid(it.code)}`}
                                className="text-[11px] font-semibold text-[var(--gold-deep)] inline-flex items-center gap-1 hover:underline py-1.5 min-h-[28px] rounded"/>
                    </div>
                    <h3 className="font-display text-lg leading-tight">{it.title}</h3>
                    <p className="text-sm text-zinc-700 leading-relaxed">{it.summary}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <KeyRules/>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="eyebrow mb-1">Part XIX · Reference</div>
        <h2 className="font-display text-2xl sm:text-3xl mb-4">Appendices A-F</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {APPENDICES_V2.map((a) => (
            <a key={a} href={`${viewUrl}#page=${APPENDICES_PAGE}`} target="_blank" rel="noreferrer"
               className={`card-warm p-4 text-sm font-semibold text-[var(--navy)] flex items-center justify-between gap-2 hover:border-[var(--gold-deep)] ${FOCUS}`}
               aria-label={`${a}: open manual at page ${APPENDICES_PAGE} (opens in a new tab)`}
               data-testid={`appendix-${a.split(" - ")[0].toLowerCase().replace(/\s/g, "-")}`}>
              <span>{a}</span><span className="text-[11px] text-[var(--gold-deep)] shrink-0">p. {APPENDICES_PAGE} →</span>
            </a>
          ))}
        </div>
        <a href={`${viewUrl}#page=${REFERENCES_PAGE}`} target="_blank" rel="noreferrer" className={`inline-block mt-4 text-sm font-semibold text-[var(--terracotta)] hover:underline rounded ${FOCUS}`} data-testid="open-references">
          Controlling and supporting references: open manual at p. {REFERENCES_PAGE} →
        </a>
      </section>

      <Contacts/>
    </div>
  );
}
